import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TodoListMember } from '@dreamai/types';
import TaskComposerSheet from '../common/TaskComposerSheet';
import type { TaskComposerValues } from '../common/TaskComposerSheet';
import { createGuestTodo, GuestApiError } from './guestApi';
import type { CreateTodoListGuestTodoResponse } from './guestApi';

interface GuestNewTaskSheetProps {
  open: boolean;
  onClose: () => void;
  token: string;
  guestUserId: string;
  /** Accepted members of the list, the guest included. */
  members: TodoListMember[];
  onCreated: (
    todo: NonNullable<CreateTodoListGuestTodoResponse['todo']>,
  ) => void;
  /**
   * The link died or this device was revoked while the sheet was open.
   * Returns true when the error was taken over by the page.
   */
  onAccessError: (err: unknown) => boolean;
}

/**
 * "New task" sheet for the guest list view. Assignment is limited to the
 * list's accepted members; the default is unassigned.
 */
const GuestNewTaskSheet: React.FC<GuestNewTaskSheetProps> = ({
  open,
  onClose,
  token,
  guestUserId,
  members,
  onCreated,
  onAccessError,
}) => {
  const { t, i18n } = useTranslation();

  const submit = async (values: TaskComposerValues) => {
    try {
      const res = await createGuestTodo(token, values);
      if (res.todo) onCreated(res.todo);
    } catch (err) {
      if (onAccessError(err)) return;
      return err instanceof GuestApiError && err.status === 400
        ? err.message
        : t('todoGuest.newTask.error');
    }
  };

  return (
    <TaskComposerSheet
      open={open}
      onClose={onClose}
      onSubmit={submit}
      className='guest-new-task-sheet'
      testIdPrefix='guest-new-task'
      locale={i18n.language}
      assignees={members.map((m) => ({
        userId: m.userId,
        name: m.name,
        label:
          m.userId === guestUserId
            ? t('todoGuest.you', { name: m.name })
            : m.name,
      }))}
      labels={{
        title: t('todoGuest.newTask.title'),
        description: t('todoGuest.newTask.descriptionLabel'),
        descriptionPlaceholder: t('todoGuest.newTask.descriptionPlaceholder'),
        dueDate: t('todoGuest.newTask.dueDateLabel'),
        assign: t('todoGuest.newTask.assignLabel'),
        unassigned: t('todoGuest.newTask.unassigned'),
        priority: t('todoGuest.newTask.priorityLabel'),
        noPriority: t('todoGuest.newTask.noPriority'),
        priorities: {
          high: t('todoGuest.newTask.priority.high'),
          medium: t('todoGuest.newTask.priority.medium'),
          low: t('todoGuest.newTask.priority.low'),
        },
        submit: t('todoGuest.newTask.submit'),
        submitting: t('todoGuest.newTask.submitting'),
      }}
    />
  );
};

export default GuestNewTaskSheet;
